import type { Field, TypeInstanceMap, VariableADTvalue } from '@lighthouse/core'
import type { FlowNode } from '@lighthouse/shared'
import {
    getAllCanBeUpstreamNodes,
    getUpstreamNodesOptions,
    PAGE_LINK_OPTION,
    type SearchApiActionConfig,
    useAtomData
} from '@lighthouse/shared'
import { useNodesData } from '@xyflow/react'
import { includes } from 'rambda'
import React, { useCallback, useMemo } from 'react'

import { otherPlatformListAtom } from '@/atoms/application/state'
import { lastPageOfStackAtom } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useFlow, useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'

import { unitList } from '../../../NodeSelector/constants'
import * as CM from '../styles'
import { extractDataFn, textRichVariableName } from '../util'
import * as SC from './styles'

type GoogleSearchApiCardProps = {
    config: SearchApiActionConfig
}

const filterType = (arg: Field | TypeInstanceMap) => {
    return (typeof arg === 'object' ? arg.innerType : arg) === 'TEXT'
}

const GoogleSearchApiCard: React.FC<GoogleSearchApiCardProps> = ({ config }) => {
    const { query } = config
    const { type } = useFlowContainerContext()
    const otherPlatformList = useAtomData(otherPlatformListAtom)

    const isInFlow = !!type
    const isNotAutomation = type !== 'automation'
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const [stackId, pageId] = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => [s?.stackId || '', s?.pageId || ''], [])
    )

    const userOption = useGetUserRecordOptions()
    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })

    const extractData = useMemo(() => extractDataFn(query), [query])

    const nodes = useNodesData(extractData.nodeIds) as FlowNode[]

    const upstreamOptions = useMemo(
        () =>
            isInFlow
                ? getUpstreamNodesOptions({
                      workflowList,
                      dataSourceList,
                      upstreamNodes: getAllCanBeUpstreamNodes(nodes || []),
                      allUpstreamNodes: nodes
                  })
                : undefined,
        [isInFlow, workflowList, dataSourceList, nodes]
    )

    const searchApiAccount = useMemo(() => otherPlatformList?.find(item => item.type.includes('SEARCH_API')), [otherPlatformList])

    if (extractData.objects.length === 0 || !searchApiAccount?.configurationComplete) {
        return null
    }

    return (
        <CM.CardItemContainer>
            <CM.FieldItemContent>
                <CM.FieldItemTitle>
                    {extractData.objects.map(item => {
                        if (item.type === 'text') {
                            return <SC.Text key={item.value}>{item.value}</SC.Text>
                        }
                        const text = textRichVariableName({
                            userOption,
                            upstreamOptions,
                            pageOptions: isNotAutomation ? [PAGE_LINK_OPTION, ...pageOptions] : undefined,
                            data: item
                        })
                        return (
                            <SC.Text color="var(--color-purple-500)" key={text}>
                                {text}
                            </SC.Text>
                        )
                    })}
                </CM.FieldItemTitle>
            </CM.FieldItemContent>
        </CM.CardItemContainer>
    )
}

export default GoogleSearchApiCard
